import pulse from '@finn-no/pulse-sdk';
import {
  debugLog,
  type PPIDObject,
  type PPIDResponse
} from '@schibsted-nmp/advertising-shared';
import {
  $ppidAtom,
  emptyPpId,
  $pageViewIdAtom
} from '@client/core/atoms/config.js';
import localStorage from '@client/core/utils/localStorage.js';

export type PulseTracker = {
  getAdvertisingIdentifiers: () => Promise<{ delta: PPIDObject }>;
};
export type TrackerData = {
  pageViewId?: string;
};

function getTracker(): Promise<PulseTracker> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      const errMsg = 'Timeout from pulse.getTracker()';
      console.warn(`[advertising-podlet] ${errMsg}`);
      reject(new Error(errMsg));
    }, 2000);

    pulse.getTracker((tracker: PulseTracker | undefined) => {
      clearTimeout(timeout);
      if (tracker) {
        resolve(tracker);
      } else {
        const errMsg = 'No tracker found from pulse.getTracker()';
        console.warn(`[advertising-podlet] ${errMsg}`);
        reject(new Error(errMsg));
      }
    });
  });
}

function getPageState(): Promise<TrackerData> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      const errMsg = 'Timeout from pulse.getPageState()';
      console.warn(`[advertising-podlet] ${errMsg}`);
      reject(new Error(errMsg));
    }, 2000);

    pulse.getPageState((pageState: TrackerData | undefined) => {
      clearTimeout(timeout);
      if (pageState) {
        resolve(pageState);
      } else {
        const errMsg = 'No page state received from pulse.getPageState()';
        console.warn(`[advertising-podlet] ${errMsg}`);
        reject(new Error(errMsg));
      }
    });
  });
}

export function getPulseData(
  pulseFunction: (tracker: PulseTracker) => Promise<string | undefined>
): (tracker: PulseTracker) => Promise<string | undefined> {
  return (tracker: PulseTracker) =>
    new Promise((resolve, reject) => {
      pulseFunction(tracker).then(resolve).catch(reject);
    });
}

export const PPID_STORAGE_NAME = 'advertising-podlet.ppid';

export async function persistPPID(): Promise<PPIDResponse> {
  try {
    const tracker = await getTracker();

    debugLog(`pulse tracker: ${JSON.stringify(tracker, null, 2)}`);

    const res = await getAdvertisingIdentifiers(tracker);

    debugLog(`PPID response: ${JSON.stringify(res, null, 2)}`);
    const ppidData = {
      ...emptyPpId,
      ...res
    } as PPIDResponse;

    if (ppidData.delta) {
      $ppidAtom.set(ppidData);
    }

    localStorage.setItem(PPID_STORAGE_NAME, ppidData);

    return ppidData;
    // Set the object on window for use in banners.tsx and other potential places
  } catch {
    return $ppidAtom.get();
  }
}

export async function setPulsePageViewId(): Promise<string | null> {
  try {
    const pageState: TrackerData = await getPageState();

    debugLog(`pulse state: ${JSON.stringify(pageState, null, 2)}`);

    // Try to extract a pageViewId from pageState if it exists
    const pageViewIdFromState = pageState.pageViewId
      ? pageState.pageViewId
      : null;

    debugLog(
      `PageViewId response: ${JSON.stringify(pageViewIdFromState, null, 2)}`
    );

    if (pageViewIdFromState) {
      $pageViewIdAtom.set(pageViewIdFromState);
    }
    return pageViewIdFromState;
  } catch {
    return $pageViewIdAtom.get();
  }
}

export function getAdvertisingIdentifiers(
  tracker: PulseTracker
): Promise<{ delta: PPIDObject }> {
  return new Promise((resolve, reject) => {
    // gets delta and xandr automatically
    tracker.getAdvertisingIdentifiers().then(resolve).catch(reject);
  });
}
